// apps/admin-ui/src/pages/DexOnRampPage.tsx
// DEX On-Ramp (Crypto Funding) management page
// ALL RIGHTS RESERVED

import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import '../styles/data-table.css';

interface DexOnRampOrder {
  id: string;
  quoteId: string;
  userId: string;
  userEmail?: string;
  status: string;
  fromToken: string;
  fromAmount: string;
  cxxrCredited: string;
  walletAddress: string;
  txHash?: string;
  networkMode: 'mainnet' | 'testnet';
  createdAt: string;
  confirmedAt?: string;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export function DexOnRampPage() {
  const [orders, setOrders] = useState<DexOnRampOrder[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [networkFilter, setNetworkFilter] = useState<string>('');

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(pagination.page),
        pageSize: String(pagination.pageSize),
      });
      if (statusFilter) params.set('status', statusFilter);
      if (networkFilter) params.set('networkMode', networkFilter);

      const response = await api.get(`/api/v1/admin/dex/onramp/orders?${params}`);
      setOrders(response.orders || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
        totalPages: Math.ceil((response.total || 0) / prev.pageSize),
      }));
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch DEX onramp orders:', err);
      setError(err.message || 'Failed to load orders');
      // Mock data for development
      setOrders([
        {
          id: '3838978000001',
          quoteId: 'q-001',
          userId: '1',
          userEmail: 'alice1@test.com',
          status: 'completed',
          fromToken: 'WETH',
          fromAmount: '0.1',
          cxxrCredited: '216.21',
          walletAddress: '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266',
          txHash: '0x0000000000000000000000000000000000000000000000000003838978000001',
          networkMode: 'testnet',
          createdAt: '2026-02-02T03:45:00Z',
          confirmedAt: '2026-02-02T03:45:05Z',
        },
        {
          id: '3838978000002',
          quoteId: 'q-002',
          userId: '2',
          userEmail: 'bob@test.com',
          status: 'pending',
          fromToken: 'ETH',
          fromAmount: '0.5',
          cxxrCredited: '1081.05',
          walletAddress: '0x70997970C51812dc3A010C7d01b50e0d17dc79C8',
          networkMode: 'testnet',
          createdAt: '2026-02-02T04:00:00Z',
        },
      ]);
      setPagination(prev => ({ ...prev, total: 2, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, statusFilter, networkFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(num);
  };

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const truncateHash = (hash: string) => {
    if (!hash) return '-';
    return `${hash.slice(0, 10)}...${hash.slice(-6)}`;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'badge-success';
      case 'failed':
      case 'mint_failed':
        return 'badge-error';
      case 'pending':
      case 'submitted':
      case 'confirming':
      case 'crediting':
        return 'badge-warning';
      default:
        return 'badge-default';
    }
  };

  const getNetworkBadgeClass = (mode: string) => {
    return mode === 'testnet' ? 'badge-testnet' : 'badge-mainnet';
  };

  return (
    <div className="data-table-page">
      <div className="page-header">
        <h1 className="page-title">DEX On-Ramp Orders</h1>
        <p className="page-subtitle">Crypto funding transactions via DEX integration</p>
      </div>

      {/* Filters */}
      <div className="table-filters">
        <div className="filter-group">
          <label htmlFor="status-filter">Status</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="confirming">Confirming</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="network-filter">Network</label>
          <select
            id="network-filter"
            value={networkFilter}
            onChange={(e) => {
              setNetworkFilter(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          >
            <option value="">All</option>
            <option value="testnet">Testnet</option>
            <option value="mainnet">Mainnet</option>
          </select>
        </div>
        <button onClick={fetchOrders} className="refresh-button">
          Refresh
        </button>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchOrders}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>User</th>
              <th>From</th>
              <th>CXXR Credited</th>
              <th>Wallet Address</th>
              <th>TX Hash</th>
              <th>Status</th>
              <th>Network</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="loading-cell">
                  Loading...
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={9} className="empty-cell">
                  No orders found
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id}>
                  <td className="mono">{order.id}</td>
                  <td>
                    <div className="user-info">
                      <span className="user-email">{order.userEmail || '-'}</span>
                      <span className="user-id">ID: {order.userId}</span>
                    </div>
                  </td>
                  <td>{order.fromAmount} {order.fromToken}</td>
                  <td className="amount-cell">{formatCurrency(order.cxxrCredited)}</td>
                  <td className="mono" title={order.walletAddress}>
                    {truncateHash(order.walletAddress)}
                  </td>
                  <td className="mono" title={order.txHash}>
                    {order.txHash ? (
                      <a
                        href={
                          order.networkMode === 'testnet'
                            ? `https://sepolia.etherscan.io/tx/${order.txHash}`
                            : `https://etherscan.io/tx/${order.txHash}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tx-link"
                      >
                        {truncateHash(order.txHash)} ↗
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getNetworkBadgeClass(order.networkMode)}`}>
                      {order.networkMode}
                    </span>
                  </td>
                  <td className="time-cell">{formatDateTime(order.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <button
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default DexOnRampPage;
